import { sendCategoryFilterEvent } from '../../utils/event-triggers';

export default {
    onCreate() {
        this.mainClass = 'str-categories';
        this.openClass = `${this.mainClass}__single--open`;
        this.takeoverClass = `${this.mainClass}__takeover`;
        this.toggleButtonSelector = `.${this.mainClass}__button`;
        this.mainListSelector = `.${this.mainClass}__list`;
        this.currentSelector = `.${this.mainClass}__single--current`;
        this.itemSelector = `:scope > li > ${this.currentSelector}`;
        this.itemParentSelector = `:scope > ul > li > ${this.currentSelector}`;
        this.optionSelector = `.${this.mainClass}__single--option`;
        this.drillButtonSelector =
            `.${this.mainClass}__toggle-collapse:not(.${this.mainClass}__toggle-collapse--fake)`;
        this.backButtonSelector = `.${this.mainClass}__sub-toggle-collapse`;
        this.subCategoriesSelector = `.${this.mainClass}__sub-list`;
        this.subCategoriesListSelector = `${this.subCategoriesSelector} > ul`;
        this.slideAnimationMS = 400;
        this.smallBreakPoint = 767;
        this.breadcrumbs = {};
        this.nextFocus = null;
        this.state = {
            open: false,
            focusDisabled: true
        };
    },
    onDestroy() {
        this.sizeObserver.disconnect();
    },
    windowResizeObserver() {
        const callback = mutations => {
            if (mutations) {
                mutations.find(mutation => {
                    if (mutation.type === 'attributes') {
                        this.setWrapperHeight();
                        document.dispatchEvent(
                            new CustomEvent('category-layer-header-event', {
                                detail: {
                                    enable: this.smallMode() && this.state.open
                                }
                            })
                        );
                        return null;
                    }
                });
            }
        };
        this.sizeObserver = new MutationObserver(callback);
        this.sizeObserver.observe(
            document.querySelector('body'),
            { attributes: true, childList: false }
        );
    },
    smallMode() {
        return window.innerWidth <= this.smallBreakPoint;
    },
    getFocusOptions() {
        return {
            preventScroll: true
        };
    },
    onMount() {
        this.mainElement = document.querySelector(`.${this.mainClass}`);
        this.body = document.querySelector('body');
        this.windowResizeObserver();

        // Close when clicked outside
        this.subscribeTo(document.body).on('click', e => {
            if (
                !this.smallMode() &&
                e.target.classList &&
                !e.target.classList.contains(`${this.mainClass}__button`) &&
                !e.target.classList.contains(`${this.mainClass}__single--current`)
            ) {
                this.closeCategories();
            }
        });

        // Enable keyboard navigation
        document.querySelectorAll(`.${this.mainClass} ul`).forEach(list => {
            this.subscribeTo(list).on('focus', () => {
                const selector = `${this.itemSelector} ${this.optionSelector}`;
                if (!this.nextFocus) {
                    list.querySelector(selector).focus(this.getFocusOptions());
                } else {
                    list.querySelectorAll(selector)[this.nextFocus].focus(this.getFocusOptions());
                    this.nextFocus = null;
                }
            });
            this.subscribeTo(list).on('keydown', (e) => {
                e.stopPropagation();
                switch (e.key) {
                    case 'Tab': this.validKeyDown(e, () => this.tabNav(list, e.shiftKey ? 'up' : 'down')); break;
                    case 'ArrowUp': this.validKeyDown(e, () => this.keyNav(list, 'up')); break;
                    case 'ArrowDown': this.validKeyDown(e, () => this.keyNav(list, 'down')); break;
                    case 'ArrowRight': this.validKeyDown(e, () => this.keyDrill(list)); break;
                    case 'ArrowLeft': this.validKeyDown(e, () => this.keyBack(list)); break;
                    case 'Escape': e.preventDefault(); this.closeCategories(); break;
                    case ' ': this.validKeyDown(e, () => document.activeElement.click()); break;
                    default: break;
                }
            });
        });
    },
    validKeyDown(e, action) {
        e.preventDefault();
        if (this.state.focusDisabled) {
            this.state.focusDisabled = false;
        } else {
            action();
        }
    },
    setWrapperHeight() {
        let highest = 0;
        document.querySelectorAll(`.${this.mainClass}, .${this.mainClass}__sub-list`).forEach(list => {
            const height = list.offsetHeight;
            if (height >= highest) {
                highest = height;
            }
        });
        document.querySelector(`.${this.mainClass}__wrapper`)
            .style.height = `${highest + 15}px`; // +15 for shadow overflow
    },
    currentFocus(list) {
        const listItems = list.querySelectorAll(this.itemSelector);
        const focusedIndex = Array.from(listItems)
            .findIndex(li => (
                li.querySelector(this.optionSelector) === document.activeElement ||
                li.querySelector(this.optionSelector) === document.activeElement.previousElementSibling
            ));
        return [focusedIndex >= 0 ? listItems[focusedIndex] : null, focusedIndex, listItems];
    },
    tabNav(list, direction) {
        const [, currentIndex, listItems] = this.currentFocus(list);
        const activeElement = document.activeElement;
        const isParent = activeElement.attributes.hasOwnProperty('data-is-parent');
        if (isParent) {
            const siblingList = activeElement.closest('.str-categories__level-wrapper')
                .querySelector(':scope > ul');
            const element = direction === 'down' ? siblingList.firstElementChild : siblingList.lastElementChild;
            return element.querySelector('.str-categories__single--current').firstElementChild
                .focus(this.getFocusOptions());
        }
        const isDrillChevron = activeElement.attributes.hasOwnProperty('data-has-children');
        const nextElementSibling = activeElement.nextElementSibling;
        const hasChildren = nextElementSibling?.attributes.hasOwnProperty('data-has-children');
        const nextIndex = currentIndex + (direction === 'up' ? -1 : 1);
        const isLevelZero = document.activeElement.closest('ul')
            .previousElementSibling?.classList?.value?.indexOf('str-categories__sub-toggle-collapse') === -1;
        if (hasChildren && direction === 'down') {
            return nextElementSibling.focus(this.getFocusOptions());
        } else if (isDrillChevron && direction === 'up') {
            return activeElement.previousElementSibling.focus(this.getFocusOptions());
        } else if (nextIndex < 0 || !listItems[nextIndex]) {
            let selector = document.activeElement.closest('ul').previousElementSibling;
            if (isLevelZero) {
                selector = selector.querySelector('.str-categories__root-category');
            }
            return selector.focus(this.getFocusOptions());
        } else if (direction === 'up') {
            const targetDrillChevron = listItems[nextIndex].querySelector(this.optionSelector).nextElementSibling;
            if (targetDrillChevron?.attributes.hasOwnProperty('data-has-children')) {
                return targetDrillChevron.focus(this.getFocusOptions());
            }
        }
        this.keyNav(list, direction, true);
    },
    keyNav(list, direction) {
        const [, currentIndex, listItems] = this.currentFocus(list);
        let nextIndex = currentIndex + (direction === 'up' ? -1 : 1);
        if (nextIndex < 0) {
            nextIndex = listItems.length - 1;
        } else if (!listItems[nextIndex]) {
            nextIndex = 0;
        }
        listItems[nextIndex].querySelector(this.optionSelector).focus(this.getFocusOptions());
    },
    keyDrill(list) {
        const [current] = this.currentFocus(list);
        const subCat = current.querySelector(this.drillButtonSelector);
        if (subCat) {
            subCat.click();
        }
    },
    keyBack(list) {
        const [current] = this.currentFocus(list);
        const currentSubCat = current.closest(this.subCategoriesSelector);
        if (currentSubCat) {
            currentSubCat.querySelector(this.backButtonSelector).click();
        }
    },
    resetCategoriesMenu: function() {
        this.body.classList.remove(this.takeoverClass);
        document.dispatchEvent(new CustomEvent('category-layer-header-event', { detail: { enable: false } }));
        document.querySelector(this.toggleButtonSelector).focus(this.getFocusOptions());
        document.querySelector(this.toggleButtonSelector).setAttribute('aria-expanded', 'false');
        document.querySelectorAll(`.${this.openClass}`).forEach(openCat => {
            openCat.classList.remove(this.openClass);
        });
    },
    closeCategories: function() {
        if (this.state.open) {
            this.state.open = false;
            this.resetCategoriesMenu();
        }
    },
    openCategories: function(ev) {
        ev.originalEvent.stopPropagation();
        this.state.open = !this.state.open;
        if (!this.state.open) {
            this.resetCategoriesMenu();
        } else {
            this.setWrapperHeight();
            this.body.classList.add(this.takeoverClass);
            document.querySelector(this.toggleButtonSelector).setAttribute('aria-expanded', 'true');
            document.dispatchEvent(
                new CustomEvent('category-layer-header-event', { detail: { enable: this.smallMode() } })
            );
            setTimeout(() => {
                document.querySelector(this.mainListSelector).focus(this.getFocusOptions());
            }, this.slideAnimationMS);
        }
    },
    toggleCategoryChildren: function(type, ev) {
        ev.originalEvent.stopPropagation();
        const levelClass = `${this.mainClass}__${type}`;
        const currentMain = ev.originalEvent.target.closest(`.${levelClass}`);
        const currentClassList = currentMain.classList;
        if (currentClassList.contains(this.openClass)) {
            currentClassList.remove(this.openClass);
            this.nextFocus = this.breadcrumbs[type];
            delete this.breadcrumbs[type];
            setTimeout(() => {
                ev.originalEvent.target
                    .closest(`.${this.mainClass} ul`)
                    .focus(this.getFocusOptions());
            }, this.slideAnimationMS);
        } else {
            this.breadcrumbs[type] = +currentMain.dataset.index;
            currentClassList.add(this.openClass);
            setTimeout(() => {
                ev.originalEvent.target
                    .closest(this.currentSelector).parentElement
                    .querySelector(this.subCategoriesListSelector)
                    .focus(this.getFocusOptions());
            }, this.slideAnimationMS);
        }
    },
    fetchCategoryListings: function(url, evt) {
        sendCategoryFilterEvent({
            url: url,
            clickEvent: evt
        });
        this.closeCategories();
    }
};
